import { c as _c } from "react/compiler-runtime";
import { ChevronDown, Keyboard } from "lucide-react";
import { AppSidebarHeader, PanelTogglePlaceholder } from "@/components/ui/app-header";
import { Skeleton } from "@repo/blacksmith-ui/skeleton";
import { cn } from "@repo/blacksmith-ui/cn";
import { SidebarPrimaryNavStatic } from "./SidebarPrimaryNav";
import { SESSION_FOOTER_CLASS, SESSION_FOOTER_LABEL_CLASS, SESSION_ITEM_INDICATOR_GUTTER_CLASS, SESSION_ITEM_ROW_CLASS, SESSION_ITEM_ROW_WRAP_CLASS, SESSION_SECTION_HEADER_BOX_CLASS } from "./sessionSidebarChrome";
import { COMPACT_SESSION_FOOTER_CLASS, COMPACT_SESSION_ITEM_INDICATOR_GUTTER_CLASS, COMPACT_SESSION_ITEM_ROW_CLASS, COMPACT_SESSION_SECTION_HEADER_BOX_CLASS } from "./compactSessionRowStyles";
import { readLeftSidebarSkeletonLayout, useIsHydrated } from "./skeletonLayout";
import type { CodesmithSidebarVariant } from "./sidebarVariant";
export const SESSION_SIDEBAR_EDGE_CLASS = "shadow-[inset_-1px_0_0_0_var(--color-border)]";
interface SidebarSkeletonProps {
  variant?: "full" | "inline";
  sidebarVariant?: CodesmithSidebarVariant;
}
const TITLE_WIDTHS = ["w-[78%]", "w-[55%]", "w-[68%]", "w-[44%]", "w-[62%]"];
function SessionItemSkeleton(t0) {
  const $ = _c(17);
  const {
    showAuthor,
    titleWidth,
    compact
  } = t0;
  if (compact) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className={COMPACT_SESSION_ITEM_INDICATOR_GUTTER_CLASS} />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    const t2 = `h-3.5 ${titleWidth} rounded`;
    let t3;
    if ($[1] !== t2) {
      t3 = <div className="flex h-[22px] min-w-0 flex-1 items-center"><Skeleton className={t2} /></div>;
      $[1] = t2;
      $[2] = t3;
    } else {
      t3 = $[2];
    }
    let t4;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <Skeleton className="h-3 w-5 shrink-0 rounded" />;
      $[3] = t4;
    } else {
      t4 = $[3];
    }
    let t5;
    if ($[4] !== t3) {
      t5 = <div className={COMPACT_SESSION_ITEM_ROW_CLASS} data-slot="session-item-skeleton">{t1}<div className="flex min-w-0 flex-1 items-center gap-2">{t3}{t4}</div></div>;
      $[4] = t3;
      $[5] = t5;
    } else {
      t5 = $[5];
    }
    return t5;
  }
  let t1;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className={SESSION_ITEM_INDICATOR_GUTTER_CLASS} />;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const t2 = `h-3.5 ${titleWidth} rounded`;
  let t3;
  if ($[7] !== t2) {
    t3 = <div className="flex h-[22px] items-center"><Skeleton className={t2} /></div>;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== showAuthor) {
    t4 = showAuthor && <Skeleton className="h-2.5 w-10 rounded" />;
    $[9] = showAuthor;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Skeleton className="h-2.5 w-12 rounded" />;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t4) {
    t6 = <div className="flex h-3 items-center gap-2">{t4}{t5}</div>;
    $[12] = t4;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t3 || $[15] !== t6) {
    t7 = <div className={SESSION_ITEM_ROW_WRAP_CLASS}><div className={SESSION_ITEM_ROW_CLASS} data-slot="session-item-skeleton">{t1}<div className="min-w-0 flex-1 overflow-hidden">{t3}{t6}</div></div></div>;
    $[14] = t3;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
}
function SectionHeaderSkeleton(t0) {
  const $ = _c(16);
  const {
    label,
    first,
    compact
  } = t0;
  if (compact) {
    const t1 = !first && "mt-4";
    let t2;
    if ($[0] !== t1) {
      t2 = cn("h-9 pb-1", t1, COMPACT_SESSION_SECTION_HEADER_BOX_CLASS);
      $[0] = t1;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    let t3;
    if ($[2] !== label) {
      t3 = <span className="type-body-xs-medium uppercase text-neutral-500 dark:text-neutral-400">{label}</span>;
      $[2] = label;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    let t4;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <ChevronDown className="size-3 text-neutral-500 dark:text-neutral-400" aria-hidden={true} />;
      $[4] = t4;
    } else {
      t4 = $[4];
    }
    let t5;
    if ($[5] !== t2 || $[6] !== t3) {
      t5 = <div className={t2}>{t3}{t4}</div>;
      $[5] = t2;
      $[6] = t3;
      $[7] = t5;
    } else {
      t5 = $[7];
    }
    return t5;
  }
  const t1 = first ? "pt-3" : "pt-5";
  let t2;
  if ($[8] !== t1) {
    t2 = cn(SESSION_SECTION_HEADER_BOX_CLASS, t1);
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== label) {
    t3 = <span className="type-uppercase-title text-neutral-500 dark:text-neutral-300">{label}</span>;
    $[10] = label;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <ChevronDown className="size-3 text-neutral-500 dark:text-neutral-400" aria-hidden={true} />;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t2 || $[14] !== t3) {
    t5 = <div className={t2}>{t3}{t4}</div>;
    $[13] = t2;
    $[14] = t3;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
}
function SidebarBodyContent(t0) {
  const $ = _c(14);
  const {
    compact
  } = t0;
  let t1;
  let t2;
  if ($[0] !== compact) {
    t1 = <SectionHeaderSkeleton label="My Sessions" first={true} compact={compact} />;
    t2 = TITLE_WIDTHS.map((w, i) => <SessionItemSkeleton key={`my-${i}`} titleWidth={w} compact={compact} />);
    $[0] = compact;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  let t3;
  if ($[3] !== compact) {
    t3 = compact && <SectionHeaderSkeleton label="Collaborating" first={false} compact={compact} />;
    $[3] = compact;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  let t5;
  if ($[5] !== compact) {
    t4 = <SectionHeaderSkeleton label="Team Sessions" first={false} compact={compact} />;
    t5 = TITLE_WIDTHS.map((w_0, i_0) => <SessionItemSkeleton key={`team-${i_0}`} showAuthor={true} titleWidth={w_0} compact={compact} />);
    $[5] = compact;
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t1 || $[9] !== t2 || $[10] !== t3 || $[11] !== t4 || $[12] !== t5) {
    t6 = <>{t1}{t2}{t3}{t4}{t5}</>;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
}
function SidebarBody(t0) {
  const $ = _c(8);
  const {
    compact
  } = t0;
  const t1 = compact ? "px-2 pb-1" : "px-2 py-1";
  let t2;
  if ($[0] !== compact) {
    t2 = <SidebarBodyContent compact={compact} />;
    $[0] = compact;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t1 || $[3] !== t2) {
    t3 = <div className="h-full overflow-hidden"><div className={t1}>{t2}</div></div>;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-[var(--sidebar)] to-transparent" />;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t3) {
    t5 = <div className="flex-1 relative min-h-0">{t3}{t4}</div>;
    $[6] = t3;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
}
export function SidebarSkeleton(t0) {
  const $ = _c(21);
  const {
    variant: t1,
    sidebarVariant
  } = t0;
  const variant = t1 === undefined ? "full" : t1;
  const isHydrated = useIsHydrated();
  let t2;
  if ($[0] !== isHydrated || $[1] !== variant) {
    t2 = variant === "full" && isHydrated ? readLeftSidebarSkeletonLayout() : null;
    $[0] = isHydrated;
    $[1] = variant;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const layout = t2;
  const resolvedVariant = sidebarVariant ?? layout?.variant ?? "legacy";
  const compact = resolvedVariant === "redesign";
  if (variant === "inline") {
    let t3;
    if ($[3] !== compact) {
      t3 = <SidebarBodyContent compact={compact} />;
      $[3] = compact;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    return t3;
  }
  if (layout && !layout.visible) {
    return null;
  }
  let t3;
  if ($[5] !== layout) {
    t3 = layout ? {
      width: layout.width
    } : undefined;
    $[5] = layout;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = layout ? "flex" : "hidden min-[1018px]:flex w-[330px]";
  let t5;
  if ($[7] !== t4) {
    t5 = cn("relative flex-col bg-sidebar overflow-hidden pointer-events-none flex-shrink-0", SESSION_SIDEBAR_EDGE_CLASS, t4);
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <AppSidebarHeader title="[code]smith" actions={<PanelTogglePlaceholder />} />;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== resolvedVariant) {
    t7 = <SidebarPrimaryNavStatic variant={resolvedVariant} />;
    $[10] = resolvedVariant;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  let t9;
  if ($[12] !== compact) {
    t8 = <SidebarBody compact={compact} />;
    t9 = compact ? <div className={COMPACT_SESSION_FOOTER_CLASS}><span className="flex size-8 items-center justify-center text-neutral-500 dark:text-neutral-400"><Keyboard className="size-4" /></span><span className="type-body-xs-regular text-neutral-500 dark:text-neutral-400">Archived</span></div> : <div className={SESSION_FOOTER_CLASS}><span className={SESSION_FOOTER_LABEL_CLASS}>Archived</span></div>;
    $[12] = compact;
    $[13] = t8;
    $[14] = t9;
  } else {
    t8 = $[13];
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t3 || $[16] !== t5 || $[17] !== t7 || $[18] !== t8 || $[19] !== t9) {
    t10 = <div style={t3} className={t5} data-slot="assistant-sidebar-skeleton">{t6}{t7}{t8}{t9}</div>;
    $[15] = t3;
    $[16] = t5;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  return t10;
}